import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, X, Loader2, ImagePlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable rooms editor used in both property registration and edit flows.
 *
 * Props:
 *  - rooms: array of room objects
 *  - onChange: (newRoomsArray) => void
 *  - testIdPrefix: optional prefix for data-testid (default 'room')
 */
const RoomsEditor = ({ rooms = [], onChange, testIdPrefix = 'room' }) => {
  const fileInputRefs = useRef({});
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const addRoom = () => {
    onChange([
      ...rooms,
      {
        category: '',
        beds: 1,
        bathrooms: 1,
        max_guests: 2,
        price: '',
        inventory: 1,
        amenities: [],
        description: '',
        photos: [],
      },
    ]);
  };

  const updateRoom = (idx, field, value) => {
    const next = [...rooms];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };

  const removeRoom = (idx) => {
    onChange(rooms.filter((_, i) => i !== idx));
  };

  const handlePhotoUpload = async (idx, files) => {
    if (!files || files.length === 0) return;
    setUploadingIdx(idx);
    try {
      const uploadedUrls = [];
      for (const f of files) {
        if (!f.type?.startsWith('image/')) {
          toast.error(`${f.name} is not an image`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${BACKEND_URL}/api/properties/upload-room-photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.success && res.data?.url) {
          uploadedUrls.push(res.data.url);
        }
      }
      if (uploadedUrls.length) {
        const next = [...rooms];
        next[idx] = {
          ...next[idx],
          photos: [...(next[idx].photos || []), ...uploadedUrls],
        };
        onChange(next);
        toast.success(`Uploaded ${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingIdx(null);
      // Clear input so the same file can be selected again later
      if (fileInputRefs.current[idx]) fileInputRefs.current[idx].value = '';
    }
  };

  const removePhoto = (idx, photoIdx) => {
    const next = [...rooms];
    const photos = [...(next[idx].photos || [])];
    photos.splice(photoIdx, 1);
    next[idx] = { ...next[idx], photos };
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}s-editor`}>
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Room Types</Label>
        <Button
          type="button"
          onClick={addRoom}
          size="sm"
          className="bg-[#7CB342] hover:bg-[#6da036] text-white"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Room
        </Button>
      </div>
      {rooms.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          Click "Add Room" to define your first room type (e.g., Deluxe Room, Suite).
        </p>
      )}
      {rooms.map((room, idx) => (
        <div
          key={room.room_id || idx}
          className="border-2 border-gray-200 rounded-lg p-4 space-y-3 bg-white"
          data-testid={`${testIdPrefix}-card-${idx}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Room #{idx + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeRoom(idx)}
              className="text-red-600 hover:text-red-700"
              data-testid={`${testIdPrefix}-remove-${idx}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category *</Label>
              <Input
                placeholder="e.g., Deluxe Room"
                value={room.category || ''}
                onChange={(e) => updateRoom(idx, 'category', e.target.value)}
                className="mt-1"
                data-testid={`${testIdPrefix}-category-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Price/Night (₹) *</Label>
              <Input
                type="number"
                min="0"
                placeholder="e.g., 2500"
                value={room.price ?? ''}
                onChange={(e) => updateRoom(idx, 'price', e.target.value)}
                className="mt-1"
                data-testid={`${testIdPrefix}-price-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Inventory (# of rooms) *</Label>
              <Input
                type="number"
                min="1"
                value={room.inventory ?? 1}
                onChange={(e) => updateRoom(idx, 'inventory', e.target.value)}
                className="mt-1"
                data-testid={`${testIdPrefix}-inventory-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Max Guests/Room</Label>
              <Input
                type="number"
                min="1"
                value={room.max_guests ?? 2}
                onChange={(e) => updateRoom(idx, 'max_guests', e.target.value)}
                className="mt-1"
                data-testid={`${testIdPrefix}-maxguests-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Beds (in each Room)</Label>
              <Input
                type="number"
                min="1"
                value={room.beds ?? 1}
                onChange={(e) => updateRoom(idx, 'beds', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Bathrooms (in each Room)</Label>
              <Input
                type="number"
                min="1"
                value={room.bathrooms ?? 1}
                onChange={(e) => updateRoom(idx, 'bathrooms', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Input
              placeholder="e.g., 18 sqm room with balcony and city view"
              value={room.description || ''}
              onChange={(e) => updateRoom(idx, 'description', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Room Photos</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(room.photos || []).map((url, photoIdx) => (
                <div key={`${url}-${photoIdx}`} className="relative w-20 h-20 rounded-md overflow-hidden border group" data-testid={`${testIdPrefix}-photo-${idx}-${photoIdx}`}>
                  <img
                    src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`}
                    alt={`Room ${idx + 1} photo ${photoIdx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=120'; }}
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx, photoIdx)}
                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`${testIdPrefix}-photo-remove-${idx}-${photoIdx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <input
                ref={(el) => { fileInputRefs.current[idx] = el; }}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(idx, Array.from(e.target.files || []))}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRefs.current[idx]?.click()}
                disabled={uploadingIdx === idx}
                className="w-20 h-20 rounded-md border-2 border-dashed border-gray-300 hover:border-[#7CB342] flex flex-col items-center justify-center text-gray-400 hover:text-[#7CB342] transition-colors disabled:opacity-50"
                data-testid={`${testIdPrefix}-upload-${idx}`}
              >
                {uploadingIdx === idx ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="h-5 w-5 mb-0.5" />
                    <span className="text-[10px]">Add</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">JPG/PNG/WebP — first photo shown on the room card.</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RoomsEditor;
